import { Navigate, Outlet } from '@tanstack/react-router';
import { useAuth } from '@/lib/auth-context';
import { ErrorState, LoadingState } from '@/components/states';
import { Button } from '@/components/Button';
import { AppShell } from './AppShell';

/**
 * Portão de entrada das rotas internas.
 *
 * A rota já barrou quem não tem token; aqui esperamos o `/auth/me` confirmar
 * quem é antes de desenhar o shell — sem isso a tela protegida pisca antes de
 * a sessão inválida ser descoberta.
 */
export function AuthenticatedShell() {
  const { status, retrySession, signOut } = useAuth();

  if (status === 'loading') {
    return (
      <div style={{ padding: 40 }}>
        <LoadingState label="Abrindo a plataforma…" />
      </div>
    );
  }

  if (status === 'anonymous') return <Navigate to="/login" />;

  // A sessão continua guardada: o problema é falar com o servidor agora.
  // Mandar para o login faria o operador digitar a senha à toa — e voltar
  // para a mesma tela de erro, porque o servidor segue sem responder.
  if (status === 'unreachable') {
    return (
      <div style={{ padding: 40 }}>
        <ErrorState
          title="Sessão não confirmada"
          message="Não foi possível falar com o servidor para confirmar sua sessão. Sua sessão continua aberta — verifique a conexão e tente de novo."
          onRetry={retrySession}
        />
        {/* Saída de emergência: sem ela, quem precisa trocar de usuário
            (ou foi desligado por outro motivo) não tinha como sair daqui —
            o /login devolve para cá enquanto houver sessão guardada. */}
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Button variant="ghost" onClick={() => void signOut()}>
            Entrar com outra conta
          </Button>
        </div>
      </div>
    );
  }

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}
